import {MessageService, Question, UserMetadata} from "@bequeathed/ngx-services";
import { LoggerService } from "@bequeathed/ngx-app-core";

const log = LoggerService.get("QuestionUtils");

/**
 * Utilities for managing question properties
 *
 * @author Dan Bennett
 */
export class QuestionUtils {

    private static answerSplit = /[\n|]/
    private static initiatorType: string | null = null
    private static initiator: string | null = null

    /**
     * Sets initiator type
     */
    public static setUserMetadata(userMetadata?: UserMetadata) {
        if (!userMetadata) return

        this.initiator = userMetadata.charityInitiator || userMetadata.lawfirmInitiator || userMetadata.guidanceInitiator || null
        this.initiatorType =
            userMetadata?.charityInitiator ? 'charity' :
                userMetadata?.lawfirmInitiator ? 'lawfirm' :
                userMetadata?.guidanceInitiator ? 'guidance' :
                null
    }

    /**
     * Finds the correct message to display for a given question. On the summary page the `summary` flag should be set to true and this function will
     * attempt to find an override if applicable.
     *
     * @param interviewType - interview type i.e. WILL, ADVICE etc
     * @param question - the question being asked
     * @param messages - message bundle
     * @param optionalSuffix - an optional message code suffix, used for video - no-video
     * @param summary - true if the summary override should be applied
     */
    public static getText(
        interviewType: string,
        question: Question,
        messages: MessageService,
        optionalSuffix?: string | null,
        summary = false,
    ): string | null {
        const idSplit = question.id!.split(/\[[0-9]*]\./);
        const id = idSplit[idSplit.length - 1];
        const suffix = optionalSuffix ? `.${optionalSuffix}` : '';
        const qualifier = summary ? 'summary' : 'text';
        let message: string | null = null;
        if (this.initiatorType) {
            message = messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}-${this.initiator}.${id}${suffix}`) ||
                messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}-${this.initiator}.${id}`);

            if (!message) {
                message = messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}.${id}${suffix}`) ||
                    messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}.${id}`);
            }
        }
        if (!message) {
            message = messages.getText(`${interviewType}.${qualifier}.${id}${suffix}`) || messages.getText(`${interviewType}.text.${id}${suffix}`);
        }
        if (!message) {
            message = messages.getText(`question.${id}${suffix}`);
            if (message) log.warn(`Legacy message code found: question.${id}${suffix}`);
        }
        if (!message && question?.source) {
            message =
                messages.getText(`${interviewType}.${qualifier}.${question.source}`)
                || messages.getText(`${interviewType}.text.${question.source}`) // In the case of `summary`
                || messages.getText(question.source);
        }
        return message;
    }

    private static compressValue(
        value?: string | null,
    ): string {
        if (!value) return ''
        return value.toLowerCase().replace(/[,:| ]/g, '')
    }

    /**
     * Finds the correct message to display for a given question option. On the summary page the `summary` flag should be set to true and this function will
     * attempt to find an override if applicable.
     *
     * @param interviewType - interview type i.e. WILL, ADVICE etc
     * @param question - the question being asked
     * @param messages - message bundle
     * @param value - the value from the option
     * @param summary - true if the summary override should be applied
     */
    public static getOptionDisplayValue(
        interviewType: string,
        question: Question,
        messages: MessageService,
        value: string,
        summary = false,
    ): string {
        const idSplit = question.id!.split(/\[[0-9]*]\./);
        const id = idSplit[idSplit.length - 1];
        const suffix = value ? `${this.compressValue(value)}` : '';
        const qualifier = summary ? 'summary-option' : 'option';
        let message: string | null = null;
        if (this.initiatorType) {
            message = messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}-${this.initiator}.${id}.${suffix}`) ||
                messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}.${id}.${suffix}`) ||
                null
        }
        if (!message) {
            message = messages.getText(`${interviewType}.${qualifier}.${id}.${suffix}`) || messages.getText(`${interviewType}.option.${id}.${suffix}`);
        }
        return message;
    }

    /**
     * Finds the correct link for the LINK control type
     *
     * @param interviewType - interview type i.e. WILL, ADVICE etc
     * @param question - the question being asked
     * @param messages - message bundle
     * @param value - the value from the option
     */
    public static getLink(
        interviewType: string,
        question: Question,
        messages: MessageService,
        value?: string,
    ): string {
        const idSplit = question.id!.split(/\[[0-9]*]\./);
        const id = idSplit[idSplit.length - 1];
        const suffix = value ? `${this.compressValue(value)}` : '';
        const qualifier = 'link';
        let message: string | null = null;
        if (this.initiatorType) {
            message = messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}-${this.initiator}.${id}.${suffix}`) ||
                messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}-${this.initiator}.${id}`) ||
                messages.getText(`${interviewType}.${qualifier}-${this.initiatorType}.${id}.${suffix}`) ||
                null
        }
        if (!message) {
            message = messages.getText(`${interviewType}.${qualifier}.${id}.${suffix}`) ||
                `MISSING :: ${interviewType}.link.${id}.${suffix}`
        }
        return message;
    }

    /**
     * Retrieves a message from the `getText()` function and then santises it to remove any markup for readability.
     *
     * @param interviewType - interview type i.e. WILL, ADVICE etc
     * @param question - the question being asked
     * @param messages - message bundle
     * @param optionalSuffix - an optional message code suffix, used for video - no-video
     * @param summary - true if the summary override should be applied
     */
    public static getPlainText(
        interviewType: string,
        question: Question,
        messages: MessageService,
        optionalSuffix?: string | null,
        summary = false,
    ): string | null {
        const text = QuestionUtils.getText(interviewType, question, messages, optionalSuffix, summary)
        return text?.replace(/<(\/|)(p(| class="field-label")|div(| class="field-description")|li|ul|b|i|strong)>/g, '') || ''
    }
    /**
     * Finds the correct message code to display for a given answer. On the summary page the `summary` flag should be set to true and this function will
     * attempt to automatically find an override if applicable.
     *
     * This should work for any answer but is primarily intended for:
     * - summary page
     * - questions with accepted values
     *
     * @param interviewType - interview type i.e. WILL, ADVICE etc
     * @param question - the question being asked
     * @param messages - message bundle
     * @param answer - the given answer or accepted value to find a message for
     * @param summary - true if the summary override should be applied
     */
    public static getAnswer(interviewType: string, question: Question, messages: MessageService, answer: string | null = null, summary = false): string {
        if (!question.summary && question.acceptedValues?.size) {
            answer = (answer || question.calculatedResponse || question.userResponse || '')
                .split(QuestionUtils.answerSplit)
                .map((answer: string) => answer.trim())
                .map((answer: string) => {
                    // Match summary override if applicable
                    let matchedMessage = summary ? messages.getText(`${interviewType}.summary.${question.id}.${answer}`) : null;
                    // Find regular option based on convention
                    if (!matchedMessage && answer) {
                        matchedMessage = this.getOptionDisplayValue(interviewType, question, messages, answer)
                        // matchedMessage = messages.getText(`${type}.option.${question.id}.${answer.toLowerCase().replace(/[,:| ]/g, '')}`);
                    }
                    // Find regular option based on message code
                    if (!matchedMessage && question?.acceptedValues?.get(answer)) {
                        matchedMessage = messages.getText(question.acceptedValues.get(answer)!);
                    }
                    if (matchedMessage) {
                        return matchedMessage;
                    }
                    // Return answer directly
                    return answer;
                })
                .join('<br>');
        } else {
            answer = (answer || question.calculatedResponse || '')
                .split(QuestionUtils.answerSplit)
                .map((answer: string) => answer.trim())
                .join('<br>');
        }

        if (question.type === 'CURRENCY' && !answer.startsWith('£')) {
            answer = '£' + answer;
        } else if (question.type === 'PERCENTAGE' && !answer.endsWith('%')) {
            answer += '%';
        }
        return answer;
    }
}
